import { useMemo } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';

import type { SandboxContract } from '../../types';

type SandboxActions = SandboxContract['actions'];
type SubmitParams = Parameters<SandboxActions['submit']>[0];
type UpdateParams = Parameters<SandboxActions['update']>[0];
type TrackingParams = Parameters<SandboxActions['track']>[0];

/**
 * Overrides how the child's `submit` message is handled. Receives the raw params
 * plus the real `submit` action, and decides if/when to call it — e.g. to gate on
 * an async step (a mutation) before committing to the state machine. When omitted,
 * `submit` dispatches directly, same as every other sandbox action.
 */
type OnSubmitOverride = (params: SubmitParams, submit: SandboxActions['submit']) => void | Promise<void>;

export function useSandbox(onSubmit?: OnSubmitOverride): SandboxContract {
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();
	const { errorReporter } = useUtilityContext();

	const sandboxData = useMemo(() => ({ draft: data.draft ?? {} }), [data.draft]);

	const sandboxActions = useMemo(() => {
		const submit: SandboxActions['submit'] = ({ draft, target }) => actions.send({ type: 'submit', draft, target });

		return {
			back: () => actions.send({ type: 'back' }),
			update: ({ draft }: UpdateParams) => actions.send({ type: 'submit', target: 'self', draft }),
			submit: onSubmit
				? (params: SubmitParams) => {
						// onSubmit may throw synchronously or reject asynchronously (e.g. a mutation);
						// wrapping the call itself in the async IIFE (rather than just its result)
						// catches both, since there's no UI at this layer to recover from an onSubmit
						// failure itself.
						(async () => onSubmit(params, submit))().catch((error: unknown) => {
							void errorReporter.report(new Error('SAP: sandbox onSubmit failed', { cause: error }));
						});
					}
				: submit,
			track: ({ eventName, eventData }: TrackingParams) => void track(eventName, eventData),
		};
	}, [actions, track, onSubmit, errorReporter]);

	return { data: sandboxData, actions: sandboxActions };
}
